import type { ColumnDef } from '@tanstack/react-table'
import { Edit, MoreHorizontal, Plus, Trash2 } from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'
import type { Agent } from '@/features/agents/types/agent.types'
import type { Entitlement } from '@/shared/components/common/EntitlementsTable'
import {
  SubtleTabs,
  SubtleTabsList,
  SubtleTabsTrigger,
} from '@/shared/components/common/tabs/SubtleTabs'
import { EnhancedTable } from '@/shared/components/table/EnhancedTable'
import { Button } from '@/shared/components/ui/button'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'
import { EntitlementCreateEdit } from './EntitlementCreateEdit'

interface EntitlementsManagementProps {
  entitlements: Entitlement[]
  setEntitlements: React.Dispatch<React.SetStateAction<Entitlement[]>>
  availableAgents: Agent[]
  editingEntitlement: Entitlement | null
  setEditingEntitlement: React.Dispatch<
    React.SetStateAction<Entitlement | null>
  >
}

export function EntitlementsManagement({
  entitlements,
  setEntitlements,
  availableAgents,
  editingEntitlement,
  setEditingEntitlement,
}: EntitlementsManagementProps) {
  // State definitions
  const [currentStep, setCurrentStep] = useState<'list' | 'create' | 'edit'>(
    'list'
  )
  const [filterType, setFilterType] = useState<'agents' | 'features'>('agents')
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [entitlementToDelete, setEntitlementToDelete] = useState<string | null>(
    null
  )

  // Define handlers before they're used in columns
  const handleStartEdit = useCallback(
    (entitlement: Entitlement) => {
      setEditingEntitlement(entitlement)
      setCurrentStep('edit')
    },
    [setEditingEntitlement]
  )

  const handleDeleteClick = useCallback((entitlementId: string) => {
    setEntitlementToDelete(entitlementId)
    setDeleteDialogOpen(true)
  }, [])

  const handleDelete = useCallback(() => {
    if (entitlementToDelete) {
      setEntitlements(prev => prev.filter(e => e.id !== entitlementToDelete))
      setDeleteDialogOpen(false)
      setEntitlementToDelete(null)
    }
  }, [entitlementToDelete, setEntitlements])

  const handleDeleteCancel = useCallback(() => {
    setDeleteDialogOpen(false)
    setEntitlementToDelete(null)
  }, [])

  // Filter entitlements based on selected type
  const filteredEntitlements = useMemo(() => {
    if (filterType === 'agents')
      return entitlements.filter(e => e.type === 'agent')
    return entitlements.filter(e => e.type === 'feature')
  }, [entitlements, filterType])

  // Create columns for the entitlements table
  const columns: ColumnDef<Entitlement>[] = useMemo(() => {
    const baseColumns: ColumnDef<Entitlement>[] = [
      {
        accessorKey: 'name',
        header: filterType === 'agents' ? 'Agent Name' : 'Feature Name',
        cell: ({ row }) => (
          <div>
            <div className='font-medium'>
              {row.original.type === 'agent'
                ? row.original.agentName
                : row.original.name}
            </div>
          </div>
        ),
        size: 200,
      },
    ]

    // Only add Signal column for agents
    if (filterType === 'agents') {
      baseColumns.push({
        accessorKey: 'signalName',
        header: 'Signal',
        cell: ({ row }) => (
          <div>
            <div className='font-medium'>{row.original.signalName}</div>
            <div className='text-xs text-muted-foreground'>
              {row.original.signalKind}
            </div>
          </div>
        ),
        size: 200,
      })
    }

    // Add remaining columns
    baseColumns.push(
      {
        accessorKey: 'value',
        header: 'Limit',
        cell: ({ row }) => (
          <div className='font-mono'>
            <div>
              {row.original.value === 'infinity' ? '∞' : row.original.value}
            </div>
            <div className='text-xs text-muted-foreground'>
              /{row.original.usageReset}
            </div>
          </div>
        ),
        size: 120,
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant='ghost' className='h-8 w-8 p-0'>
                <MoreHorizontal className='h-4 w-4' />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end'>
              <DropdownMenuItem onClick={() => handleStartEdit(row.original)}>
                <Edit className='mr-2 h-4 w-4' />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => handleDeleteClick(row.original.id)}
                className='text-red-600'
              >
                <Trash2 className='mr-2 h-4 w-4' />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ),
        size: 80,
      }
    )

    return baseColumns
  }, [handleStartEdit, handleDeleteClick, filterType])

  const handleStartCreate = () => {
    setEditingEntitlement(null)
    setCurrentStep('create')
  }

  const handleCancel = () => {
    setCurrentStep('list')
    setEditingEntitlement(null)
  }

  const handleEntitlementSave = (entitlement: Entitlement) => {
    if (editingEntitlement) {
      setEntitlements(prev =>
        prev.map(e => (e.id === entitlement.id ? entitlement : e))
      )
    } else {
      setEntitlements(prev => [...prev, entitlement])
    }
    setCurrentStep('list')
    setEditingEntitlement(null)
  }

  if (currentStep === 'create' || currentStep === 'edit') {
    return (
      <EntitlementCreateEdit
        availableAgents={availableAgents}
        editingEntitlement={editingEntitlement}
        onSave={handleEntitlementSave}
        onCancel={handleCancel}
      />
    )
  }

  return (
    <div className='space-y-6'>
      <Card className='bg-transparent border-0 shadow-none'>
        <CardHeader>
          <div className='flex items-center justify-between'>
            <div>
              <CardTitle className='flex items-center gap-2'>
                Plan Entitlements
              </CardTitle>
              <p className='text-sm text-muted-foreground'>
                Feature and agent access limits configured for this plan
              </p>
            </div>
            <Button onClick={handleStartCreate}>
              <Plus className='w-4 h-4 mr-2' />
              Add Entitlement
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className='space-y-4'>
            {/* Filter tabs */}
            <SubtleTabs
              value={filterType}
              onValueChange={(value: string) =>
                setFilterType(value as 'agents' | 'features')
              }
            >
              <SubtleTabsList className='grid w-full grid-cols-2'>
                <SubtleTabsTrigger value='agents'>Agents</SubtleTabsTrigger>
                <SubtleTabsTrigger value='features'>Features</SubtleTabsTrigger>
              </SubtleTabsList>
            </SubtleTabs>

            <div className='[&_table]:bg-transparent [&_thead]:bg-transparent [&_tr:hover]:bg-slate-50/50 dark:[&_tr:hover]:bg-slate-800/50'>
              <EnhancedTable
                key='entitlements-table'
                data={filteredEntitlements}
                columns={columns}
                height='calc(100vh - 500px)'
                className='bg-transparent border-0'
                onRowClick={row => handleStartEdit(row)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Entitlement</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this entitlement? This action
              cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant='outline' onClick={handleDeleteCancel}>
              Cancel
            </Button>
            <Button variant='destructive' onClick={handleDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
